import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { advisorService } from '../services/api'
import { getDisplayName, getInitials } from '../utils/displayName'
import { 
  ChevronDownIcon, 
  Bars3Icon, 
  XMarkIcon, 
  RectangleStackIcon, 
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline'

type NavItem = {
  name: string
  target: string
  block?: ScrollLogicalPosition
}

const Navbar = () => {
  const navigate = useNavigate()
  const routerLocation = useLocation()
  const { user, isAuthenticated, logout, isImpersonating, stopImpersonation } = useAuth()
  const [toggle, setToggle] = useState(false)
  const [userDropdown, setUserDropdown] = useState(false)
  const [isDesktop, setIsDesktop] = useState(false)
  const [navVisible, setNavVisible] = useState(false)
  const [advisorUnreadCount, setAdvisorUnreadCount] = useState(0)
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const displayName = getDisplayName(user)
  const initials = getInitials(user)
  
  // Navigation items data with scroll targets
  const navItems: NavItem[] = [
    { name: 'OUR PROGRAMS', target: 'programs', block: 'start' },
    { name: 'STUDENT EXPERIENCE', target: 'student-experience', block: 'start' },
    { name: 'OUR LEGACY', target: 'legacy-timeline', block: 'start' }
  ]
  
  // Admin management links (desktop only)
  const adminLinks = user?.is_admin ? (
    <ul className="flex flex-nowrap gap-4 ml-8 whitespace-nowrap">
      <li>
        <button
          onClick={() => navigate('/admin/users')}
          className="text-white hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-4 py-2 text-sm font-medium"
        >
          Manage Users
        </button>
      </li>
      <li>
        <button
          onClick={() => navigate('/admin/programs')}
          className="text-white hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-4 py-2 text-sm font-medium"
        >
          Manage Programs
        </button>
      </li>
      <li>
        <button
          onClick={() => navigate('/admin/scholarships-management')}
          className="text-white hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-4 py-2 text-sm font-medium"
        >
          Manage Scholarships
        </button>
      </li>
    </ul>
  ) : null;
  
  const showNavTemporarily = useCallback((duration = 2500) => {
    setNavVisible(true)
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
    }
    hideTimerRef.current = setTimeout(() => {
      setNavVisible(false)
    }, duration)
  }, [])

  // Smooth scroll function
  const scrollToSection = (targetId: string, block: ScrollLogicalPosition = 'start') => {
    const element = document.getElementById(targetId)
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block
      })
    }
    // Close mobile menu if open
    setToggle(false)
    showNavTemporarily()
  }
  
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024)
    }
    
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdown && !(event.target as Element).closest('.user-dropdown')) {
        setUserDropdown(false)
      }
    }
    const handleScroll = () => showNavTemporarily()
    const handleMouseMove = () => showNavTemporarily()
    
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('mousemove', handleMouseMove)
    showNavTemporarily(3000)
    
    return () => {
      window.removeEventListener('resize', checkScreenSize)
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('mousemove', handleMouseMove)
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current)
      }
    }
  }, [userDropdown, showNavTemporarily])
  
  const handleClick = () => {
    const nextState = !toggle
    setToggle(nextState)
    if (nextState) {
      setNavVisible(true)
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    } else {
      showNavTemporarily()
    }
  }
  
  const handleLogout = async () => {
    try {
      await logout()
      // User will be redirected by auth state change
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleBrandClick = () => {
    if (routerLocation.pathname === '/') {
      scrollToSection('hero', 'start')
    } else {
      navigate('/')
    }
    showNavTemporarily()
  }

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null
    const unreadPollMs = process.env.NODE_ENV === 'production' ? 60000 : 5000
    const loadUnread = async () => {
      if (!isAuthenticated || !user || user.is_admin) {
        setAdvisorUnreadCount(0)
        return
      }
      try {
        const data = await advisorService.getUnreadCount()
        setAdvisorUnreadCount(data.unread_count || 0)
      } catch {
        // Ignore unread count errors to avoid breaking nav.
      }
    }
    loadUnread()
    if (isAuthenticated && user && !user.is_admin) {
      interval = setInterval(loadUnread, unreadPollMs)
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadUnread()
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      if (interval) clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [isAuthenticated, user])

  const goToAdvisorMessages = () => {
    navigate('/student-pathway#advisor-support')
    setUserDropdown(false)
    setToggle(false)
  }

  // Reusable Navigation Items Component
  const NavigationItems = ({ isMobile = false }) => (
    <ul className={isMobile ? 'space-y-1' : 'flex flex-nowrap gap-8 lg:gap-10 text-white whitespace-nowrap'}>
      {navItems.map((item) => (
        <li 
          key={item.name}
          onClick={() => scrollToSection(item.target, item.block)}
          className={
            isMobile 
              ? 'p-4 text-white hover:text-emerald-300 hover:bg-white/10 cursor-pointer rounded-lg transition-all duration-200'
              : 'hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-6 py-3 text-sm font-medium relative group'
          }
        >
          {item.name}
          {!isMobile && (
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-300 transition-all duration-300 group-hover:w-full"></span>
          )}
        </li>
      ))}
      {user?.is_admin && (
        <li>
          <button
            onClick={() => navigate('/admin/scholarships-management')}
            className={
              isMobile
                ? 'p-4 text-white hover:text-emerald-300 hover:bg-white/10 cursor-pointer rounded-lg transition-all duration-200'
                : 'hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-6 py-3 text-sm font-medium relative group'
            }
          >
            Admin: Manage Scholarships
            {!isMobile && (
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-300 transition-all duration-300 group-hover:w-full"></span>
            )}
          </button>
        </li>
      )}
      {user?.is_admin && (
        <li>
          <button
            onClick={() => navigate('/admin/users')}
            className={
              isMobile
                ? 'p-4 text-white hover:text-emerald-300 hover:bg-white/10 cursor-pointer rounded-lg transition-all duration-200'
                : 'hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-6 py-3 text-sm font-medium relative group'
            }
          >
            Admin: Manage Users
            {!isMobile && (
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-300 transition-all duration-300 group-hover:w-full"></span>
            )}
          </button>
        </li>
      )}
    </ul>
  )

  // Reusable Auth Buttons Component
  const AuthButtons = ({ isMobile = false }) => (
    <div className={isMobile ? 'flex flex-col mt-6 gap-3 border-t border-white/20 pt-4' : 'flex items-center gap-4'}>
      {isAuthenticated ? (
        <div className={isMobile ? 'w-full' : 'relative user-dropdown'}>
          {isMobile ? (
            /* Mobile - Stack vertically */
            <div className="space-y-3">
              <div className="text-white text-sm font-medium px-4 py-2 text-center bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                Hi, {displayName}!
              </div>
              <button
                onClick={() => {
                  if (user?.is_admin) navigate('/admin/dashboard'); else if (user?.role === 'professor') navigate('/professor-pathway'); else navigate('/student-pathway')
                  setToggle(false)
                }}
                className="bg-gradient-to-br from-purple-600/90 to-purple-700/90 text-white py-3 px-4 rounded-lg font-semibold hover:from-purple-700/90 hover:to-purple-800/90 transition-all duration-200 w-full h-12 flex items-center justify-center"
              >
                {user?.is_admin ? 'Dashboard' : 'My Pathway'}
              </button>
              {!user?.is_admin && user?.role !== 'professor' && (
                <button
                  onClick={goToAdvisorMessages}
                  className="bg-white/10 border border-white/20 text-white py-3 px-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-200 w-full h-12 flex items-center justify-center gap-2"
                >
                  Advisor Messages
                  {advisorUnreadCount > 0 && (
                    <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-amber-900 text-xs font-bold flex items-center justify-center">
                      {advisorUnreadCount}
                    </span>
                  )}
                </button>
              )}
              {isImpersonating && (
                <button
                  onClick={() => {
                    stopImpersonation()
                    setToggle(false)
                  }}
                  className="bg-gradient-to-br from-amber-500/90 to-amber-600/90 text-white py-3 px-4 rounded-lg font-semibold hover:from-amber-600/90 hover:to-amber-700/90 transition-all duration-200 w-full h-12 flex items-center justify-center"
                >
                  Return to Admin
                </button>
              )}
              <button
                onClick={() => {
                  handleLogout()
                  setToggle(false)
                }}
                className="bg-gradient-to-br from-red-600/90 to-red-700/90 text-white py-3 px-4 rounded-lg font-semibold hover:from-red-700/90 hover:to-red-800/90 transition-all duration-200 w-full h-12 flex items-center justify-center"
              >
                Logout
              </button>
            </div>
          ) : (
            /* Desktop - Dropdown */
            <div className="relative">
              <button
                onClick={() => setUserDropdown(!userDropdown)}
                className="flex items-center text-white font-medium hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-4 py-2 text-sm bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 hover:bg-white/20"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                    {initials}
                  </div>
                  <span>Hi, {displayName}!</span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${userDropdown ? 'rotate-180' : ''}`} />
                </div>
              </button>
              
              {userDropdown && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900/95 backdrop-blur-lg border border-white/20 rounded-lg shadow-xl py-2 z-50">
                  <button
                    onClick={() => {
                      if (user?.is_admin) navigate('/admin/dashboard'); else if (user?.role === 'professor') navigate('/professor-pathway'); else navigate('/student-pathway')
                      setUserDropdown(false)
                    }}
                    className="w-full text-left px-4 py-2 text-white hover:bg-white/10 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <RectangleStackIcon className="w-4 h-4" />
                    <span>{user?.is_admin ? 'Dashboard' : 'My Pathway'}</span>
                  </button>
                  {!user?.is_admin && user?.role !== 'professor' && (
                    <button
                      onClick={goToAdvisorMessages}
                      className="w-full text-left px-4 py-2 text-white hover:bg-amber-600/20 transition-colors duration-200 flex items-center justify-between"
                    >
                      <span>Advisor Messages</span>
                      {advisorUnreadCount > 0 && (
                        <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-amber-400 text-amber-900 text-xs font-bold flex items-center justify-center">
                          {advisorUnreadCount}
                        </span>
                      )}
                    </button>
                  )}
                  {isImpersonating && (
                    <button
                      onClick={() => {
                        stopImpersonation()
                        setUserDropdown(false)
                      }}
                      className="w-full text-left px-4 py-2 text-white hover:bg-amber-600/20 transition-colors duration-200 flex items-center space-x-2"
                    >
                      <ArrowRightOnRectangleIcon className="w-4 h-4" />
                      <span>Return to Admin</span>
                    </button>
                  )}
                  <div className="border-t border-white/20 my-1"></div>
                  <button
                    onClick={() => {
                      handleLogout()
                      setUserDropdown(false)
                    }}
                    className="w-full text-left px-4 py-2 text-white hover:bg-red-600/20 transition-colors duration-200 flex items-center space-x-2"
                  >
                    <ArrowRightOnRectangleIcon className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <button
          onClick={() => {
            navigate('/login')
            if (isMobile) setToggle(false)
          }}
          className={
            isMobile
              ? "text-white py-3 px-4 rounded-lg font-medium hover:text-emerald-300 hover:bg-white/10 transition-all duration-200 w-full text-center"
              : "text-white font-medium hover:text-emerald-300 transition-colors duration-200 cursor-pointer px-6 py-3 text-sm relative group"
          }
        >
          Sign In / Create Account
          {!isMobile && (
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-emerald-300 transition-all duration-300 group-hover:w-full"></span>
          )}
        </button>
      )}
    </div>
  )

  const navVisibilityClass = (navVisible || toggle) 
    ? 'translate-y-0 opacity-100 pointer-events-auto' 
    : '-translate-y-full opacity-0 pointer-events-none'

  return (
    <div 
      className={`fixed top-0 left-0 w-full h-20 backdrop-blur-lg border-b border-white/20 shadow-xl bg-black/30 transition-all duration-300 transform ${navVisibilityClass}`}
      style={{ zIndex: 10000 }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 1.0)'
        setNavVisible(true)
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.3)'
        if (!toggle) {
          showNavTemporarily()
        }
      }}
    >
      <nav className='relative w-full px-4 lg:px-12 flex justify-between items-center h-20'>
          
        {/* Logo/Brand */}
        <div 
          className='flex-shrink-0 flex items-center cursor-pointer hover:opacity-80 transition-opacity duration-200'
          onClick={handleBrandClick}
        >
          <img 
            src="/logo.png" 
            alt="Muslim Study Abroad Logo" 
            className="h-10 w-auto mr-3"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
          <h1 className='text-white text-xl font-bold tracking-wide'>
            Muslim Study Abroad
          </h1>
        </div>
          
                  
        {/* Desktop Navigation - Show ONLY on large screens (1024px+) */}
        <div 
          className='items-center gap-4 overflow-x-auto whitespace-nowrap pr-2'
          style={{ 
            display: isDesktop ? 'flex' : 'none' 
          }}
        >
          <NavigationItems isMobile={false} />
          {adminLinks}
        </div>
        
        {/* Desktop Auth Buttons - Show ONLY on large screens (1024px+) */}
        <div 
          style={{ 
            display: isDesktop ? 'flex' : 'none' 
          }}
        >
          <AuthButtons isMobile={false} />
        </div>
        
        {/* Mobile Hamburger Menu Button - Show ONLY on mobile/tablet (below 1024px) */}
        <div 
          className='cursor-pointer'
          style={{ 
            display: isDesktop ? 'none' : 'flex' 
          }}
          onClick={handleClick}
        >
            <button className='p-2 text-white hover:text-emerald-300 hover:bg-white/10 rounded-md transition-colors duration-200'>
                {toggle ? (
                    // Close icon
                    <XMarkIcon className="h-6 w-6" />
                ) : (
                    // Hamburger icon
                    <Bars3Icon className="h-6 w-6" />
                )}
            </button>
        </div>
      </nav>
      
      {/* Mobile Dropdown Menu - Show ONLY on mobile/tablet (below 1024px) */}
      {toggle && !isDesktop && (
        <div 
          className='absolute top-full left-0 w-full bg-gray-900 border-t border-white/20 shadow-2xl'
          style={{ zIndex: 10001 }}
        >
          <div className='p-4'>
            <NavigationItems isMobile={true} />
            <AuthButtons isMobile={true} />
          </div>
        </div>
      )}
    </div>
  )
}

export default Navbar
